import { z } from 'zod'
import { dateInZone } from './delivery-schedule'
import { plainTextAsHtml } from './report'

/**
 * The global notice (CLI-625): one message Clipbook staff put into EVERY report for a while —
 * "I hope you're well! For your awareness, we will pause report delivery Monday, October 12 …
 * Sharing press clips for today." The legacy portal kept it as the Firestore document
 * `email_body/current_email_body`, and `getEmailBody` rendered it AS the intro paragraph in place
 * of "Sharing press clips for today." — which is why CS write the notice to end with that line.
 * Headline keeps those semantics, because CS already know them: while the notice reads, it is the
 * intro; `overrideReportIntro` is legacy's `global_override`, deciding whether it also replaces a
 * report's own intro or yields to it.
 *
 * Absent or inactive = no notice. The window is a pair of calendar dates, judged in each report's
 * own timezone at render time — an improvement over legacy's manual toggle: "through Monday" has
 * to mean Monday where the client is, and nobody has to remember to take it out.
 */
export const GLOBAL_NOTICE_MAX_LENGTH = 5000

export const globalNoticeSchema = z.object({
  /** Plain text from the Settings textarea; blank lines separate paragraphs. */
  body: z.string(),
  /**
   * Legacy's `global_override`. On, the notice replaces a report's OWN intro too; off, a report
   * that wrote its own intro keeps it and shows no notice, and only reports following their
   * template's intro carry the notice.
   */
  overrideReportIntro: z.boolean(),
  /** First calendar day the notice reads (inclusive), or null for "from now". */
  startsOn: z.iso.date().nullable(),
  /** Last calendar day the notice reads (inclusive), or null for "until switched off". */
  endsOn: z.iso.date().nullable(),
  updatedBy: z.string().nullable(),
  updatedAt: z.iso.datetime().nullable(),
})
export type GlobalNotice = z.infer<typeof globalNoticeSchema>

export const setGlobalNoticeSchema = z
  .object({
    body: z.string().max(GLOBAL_NOTICE_MAX_LENGTH),
    overrideReportIntro: z.boolean().default(false),
    startsOn: z.iso.date().nullable().default(null),
    endsOn: z.iso.date().nullable().default(null),
  })
  .refine((input) => !input.startsOn || !input.endsOn || input.startsOn <= input.endsOn, {
    message: 'The notice cannot end before it starts.',
    path: ['endsOn'],
  })
export type SetGlobalNotice = z.infer<typeof setGlobalNoticeSchema>

/** What a reader sees before anyone has saved a notice: nothing to say. */
export function emptyGlobalNotice(): GlobalNotice {
  return {
    body: '',
    overrideReportIntro: false,
    startsOn: null,
    endsOn: null,
    updatedBy: null,
    updatedAt: null,
  }
}

/** The fields that decide whether a notice reads; a stored row and the API shape both carry them. */
export type NoticeWindow = Pick<GlobalNotice, 'body' | 'startsOn' | 'endsOn'>

/** The window plus the one rule about reports with their own intro — what a render needs. */
export type NoticeRule = NoticeWindow & Pick<GlobalNotice, 'overrideReportIntro'>

/**
 * Whether the notice reads on `localDate` (`YYYY-MM-DD`) — a calendar day, so the comparison is
 * the ISO strings' own order. Off, blank, or outside the window means no.
 */
export function noticeShowsOn(notice: NoticeWindow | null | undefined, localDate: string): boolean {
  if (!notice || notice.body.trim() === '') return false
  if (notice.startsOn !== null && localDate < notice.startsOn) return false
  if (notice.endsOn !== null && localDate > notice.endsOn) return false
  return true
}

/** Whether the notice reads in a report rendered now, by that report's own calendar. */
export function noticeShowsIn(
  notice: NoticeWindow | null | undefined,
  timeZone: string,
  now: Date = new Date(),
): boolean {
  return noticeShowsOn(notice, dateInZone(timeZone, now))
}

/**
 * Whether a report carries the notice as its intro: it has to read today, and a report that wrote
 * its own intro keeps it unless the notice overrides — legacy's `getEmailBody` priority.
 */
export function noticeReplacesIntro(
  notice: NoticeRule | null | undefined,
  report: { timeZone: string; hasOwnIntro: boolean },
  now: Date = new Date(),
): boolean {
  if (!notice || !noticeShowsIn(notice, report.timeZone, now)) return false
  return !report.hasOwnIntro || notice.overrideReportIntro
}

/** The textarea's text as the paragraphs the email shows, escaped — the notice is never markup. */
export function noticeHtml(body: string): string {
  return plainTextAsHtml(body.trim())
}
